import classNames from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import React, { useCallback, useState, useMemo, useRef } from 'react';
import { useUpdate } from '../_utils/hooks';
import { createPrefixCls } from '../_utils/string';
import PickerCol from './picker-col';
import { getYears, getMonths, getDays, convertDate, deleteDateUnit } from './utils';
const prefixCls = createPrefixCls('picker');
export const DateRangePicker = ({ defaultValue, dateRange, dateCols = ['year', 'month', 'day'], step, onChange }) => {
    const defaultValueRef = useRef(defaultValue);
    const [pickerValue, setPickerValue] = useState(defaultValueRef.current);
    const pickerIndex = useRef();
    const [pickingIndex, setPickingIndex] = useState(0);
    useUpdate(() => {
        pickerIndex.current && onChange && onChange(pickerValue, pickerIndex.current);
    }, [onChange, pickerIndex, pickerValue]);
    const data = useMemo(() => {
        var _a, _b, _c;
        const arr = [];
        if (dateCols.includes('year')) {
            arr.push({
                col: 'year',
                data: getYears({
                    range: dateRange,
                    step: step.year
                })
            });
        }
        if (dateCols.includes('month')) {
            arr.push({
                col: 'month',
                data: getMonths({
                    year: (_a = pickerValue[pickingIndex].year) === null || _a === void 0 ? void 0 : _a.value,
                    range: dateRange,
                    step: step.month
                })
            });
        }
        if (dateCols.includes('day')) {
            arr.push({
                col: 'day',
                data: getDays({
                    year: (_b = pickerValue[pickingIndex].year) === null || _b === void 0 ? void 0 : _b.value,
                    month: (_c = pickerValue[pickingIndex].month) === null || _c === void 0 ? void 0 : _c.value,
                    range: dateRange,
                    step: step.day
                })
            });
        }
        return arr;
    }, [dateCols, dateRange, pickerValue, pickingIndex, step.day, step.month, step.year]);
    const handleChange = useCallback((el) => {
        return (index) => {
            var _a, _b;
            const col = el.col;
            pickerIndex.current = col;
            pickerValue[pickingIndex][col] = el.data[index];
            if (col === 'year' && dateCols.includes('month')) {
                const nextYearMonths = getMonths({
                    year: pickerValue[pickingIndex].year.value,
                    range: dateRange
                });
                if (!(nextYearMonths && nextYearMonths.find((el) => el.value === pickerValue[pickingIndex].month.value))) {
                    pickerValue[pickingIndex].month = nextYearMonths[0];
                }
            }
            if ((col === 'year' || col === 'month') && dateCols.includes('day')) {
                const nextMonthDays = getDays({
                    year: (_a = pickerValue[pickingIndex].year) === null || _a === void 0 ? void 0 : _a.value,
                    month: (_b = pickerValue[pickingIndex].month) === null || _b === void 0 ? void 0 : _b.value,
                    range: dateRange
                });
                if (!(nextMonthDays && nextMonthDays.find((el) => el.value === pickerValue[pickingIndex].day.value))) {
                    pickerValue[pickingIndex].day = nextMonthDays[0];
                }
            }
            const now = new Date();
            const startDate = convertDate(`
            ${pickerValue[0].year ? pickerValue[0].year.value : now.getFullYear()}
            -
            ${pickerValue[0].month ? pickerValue[0].month.value : now.getMonth() + 1}
            -
            ${pickerValue[0].day ? pickerValue[0].day.value : now.getDate()}
          `.replace(/\/n|\s+/g, ''));
            const endDate = convertDate(`
            ${pickerValue[1].year ? pickerValue[1].year.value : now.getFullYear()}
            -
            ${pickerValue[1].month ? pickerValue[1].month.value : now.getMonth() + 1}
            -
            ${pickerValue[1].day ? pickerValue[1].day.value : now.getDate()}
          `.replace(/\/n|\s+/g, ''));
            if (startDate.getTime() > endDate.getTime()) {
                pickerValue[0] = cloneDeep(pickerValue[pickingIndex]);
                pickerValue[1] = cloneDeep(pickerValue[pickingIndex]);
            }
            setPickerValue(cloneDeep(pickerValue));
        };
    }, [dateCols, dateRange, pickerValue, pickingIndex]);
    const handleTogglePickingIndex = useCallback((index) => {
        return () => {
            setPickingIndex(index);
        };
    }, []);
    const renderDateRange = useMemo(() => {
        return (React.createElement("div", { className: prefixCls('date-range') },
            React.createElement("div", { className: classNames(prefixCls('start-date'), pickingIndex === 0 && prefixCls('start-date-active')), onClick: handleTogglePickingIndex(0) }, deleteDateUnit(`
              ${pickerValue[0].year ? pickerValue[0].year.text : ''}
              ${pickerValue[0].year && pickerValue[0].month ? '-' : ''}
              ${pickerValue[0].month ? pickerValue[0].month.text : ''}
              ${pickerValue[0].day ? '-' : ''}
              ${pickerValue[0].day ? pickerValue[0].day.text : ''}
          `)),
            React.createElement("div", { className: prefixCls('delimiter') }, "\u81F3"),
            React.createElement("div", { className: classNames(prefixCls('end-date'), pickingIndex === 1 && prefixCls('start-date-active')), onClick: handleTogglePickingIndex(1) }, deleteDateUnit(`
            ${pickerValue[1].year ? pickerValue[1].year.text : ''}
            ${pickerValue[1].year && pickerValue[1].month ? '-' : ''}
            ${pickerValue[1].month ? pickerValue[1].month.text : ''}
            ${pickerValue[1].day ? '-' : ''}
            ${pickerValue[1].day ? pickerValue[1].day.text : ''}
          `))));
    }, [handleTogglePickingIndex, pickerValue, pickingIndex]);
    return (React.createElement(React.Fragment, null,
        renderDateRange,
        React.createElement("div", { className: prefixCls('content') },
            React.createElement("div", { className: prefixCls('mask') }),
            React.createElement("div", { className: prefixCls('indicator') }),
            React.createElement("div", { className: prefixCls('cols') }, data.map((el) => {
                return (React.createElement(PickerCol, { key: el.col, data: el.data, value: pickerValue[pickingIndex][el.col], onChange: handleChange(el) }));
            })))));
};
export default DateRangePicker;
